import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { withAuth } from "@cdk-prod/fortellis-auth-context";
import { useHistory } from "react-router-dom";
import WithNavigationToHome from "../components/WithNavigationToHome";
import { Checkbox, SearchInput, INPUT_SIZES } from "cdk-radial";
import UserRoleManagementPagination from "./UserRoleManagementPagination";
import CircleXIcon from "../components/CircleXIcon";
import "./style.css";

const UserRoleManagement = ({
  auth,
  fetchAllRoles,
  fetchEntityUserRoles,
  allRoles,
  entityUserRoles,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const history = useHistory();

  useEffect(() => {
    const authToken = auth?.accessToken;
    if (authToken) {
      fetchAllRoles({ accessToken: authToken });
      fetchEntityUserRoles({
        accessToken: authToken,
        entityId: "*",
        type: "platform",
      });
    }
  }, [auth, fetchAllRoles, fetchEntityUserRoles]);

  const sortedRoles = useMemo(
    () => [...(allRoles || [])].sort((a, b) => a.name.localeCompare(b.name)),
    [allRoles]
  );

  const sortedUsers = useMemo(
    () =>
      [...(entityUserRoles || [])].sort((a, b) =>
        a.displayName.localeCompare(b.displayName)
      ),
    [entityUserRoles]
  );

  const lowerSearch = searchTerm.toLowerCase().trim();

  const filteredUsers = useMemo(() => {
    const validUsers = sortedUsers.filter(
      (u) => u.displayName && u.email
    );

    if (!lowerSearch) return validUsers;

    return validUsers.filter(
      (u) =>
        u.displayName.toLowerCase().includes(lowerSearch) ||
        u.email.toLowerCase().includes(lowerSearch)
    );
  }, [sortedUsers, lowerSearch]);

  const filteredRoles = sortedRoles;
  const totalPages = Math.ceil(filteredUsers.length / pageSize);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const nameBoxWidth = 180;
  const roleBoxWidth = 96;
  const rowHeight = 60;
  const gridWidth = nameBoxWidth + filteredRoles.length * roleBoxWidth;

  const downloadCSV = () => {
    const headers = ["User", ...filteredRoles.map((r) => r.name)];
    const rows = filteredUsers.map((u) => {
      const userLabel = `${u.displayName} (${u.email})`;
      const roleValues = filteredRoles.map((r) =>
        u.roleIds?.includes(r.id) ? "Y" : "N"
      );
      return [`"${userLabel}"`, ...roleValues];
    });
    const csvContent = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "user_roles.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSearchChange = (val) => {
    setSearchTerm(val || "");
    setCurrentPage(1);
  };

  const goToUserProfileManager = (userObj) => {
    if (userObj?.email) {
      history.push(`/user-role-management/user/${userObj.email}`);
    }
  };

  return (
    <div className="user-role-wrapper">
      <div className="user-role-container">
        <div className="user-role-header">
          <h1>User Role Management</h1>
        </div>

        <p className="user-role-description">
          Get a comprehensive view of all users, including their assigned
          statements and entitlements. Easily review, edit, or duplicate
          roles to ensure accurate and consistent role management
          across the organization.
        </p>

        <div className="search-and-download">
          <SearchInput
            clearButtonLabel="Clear"
            id="search-input-expanded"
            name="search-input-expanded"
            isAlwaysExpanded
            placeholder="Search by user name or email..."
            size={INPUT_SIZES.STANDARD}
            value={searchTerm}
            onChange={handleSearchChange}
            className="search-input"
          />
          <button className="download-csv-button" onClick={downloadCSV}>
            Download Report
          </button>
        </div>

        <div className="user-role-table-container">
          <div
            className="user-role-table"
            style={{ minWidth: `${gridWidth}px` }}
          >
            <div className="user-role-table-header">
              <div className="user-id-header">
                <span className="user-id-header-name">User Details</span>
                <span className="user-email"></span>
              </div>
              {filteredRoles.map((role, i) => (
                <div
                  key={`header_${role.id}_${i}`}
                  className="role-header"
                  style={{
                    width: `${roleBoxWidth}px`,
                    height: `${rowHeight * 2}px`,
                  }}
                >
                  <div
                    className="role-header-text clickable-role-name"
                    style={{
                      maxHeight: `${rowHeight * 2 - 8}px`,
                      cursor: "pointer",
                      color: "#5a2ea6",
                      userSelect: "none",
                    }}
                    onClick={() => {
                      history.push({
                        pathname: "/role-detail",
                        state: {
                          role,
                          allUsers: filteredUsers,
                        },
                      });
                    }}
                    role="button"
                  >
                    {role.name}
                  </div>
                </div>
              ))}
            </div>

            {paginatedUsers.map((user) => (
              <div key={user.userId} className="user-role-row">
                <div className="user-id-cell overflow-cell">
                  <strong
                    className="user-name clickable-user-name"
                    onClick={() => goToUserProfileManager(user)}
                    role="button"
                    tabIndex={0}
                  >
                    {user.displayName}
                  </strong>
                  <span className="user-email">{user.email}</span>
                </div>
                {filteredRoles.map((role, i) => (
                  <div
                    key={`role_${user.userId}_${role.id}_${i}`}
                    className="role-cell"
                    style={{
                      width: `${roleBoxWidth}px`,
                      height: `${rowHeight}px`,
                    }}
                  >
                    {user.roleIds?.includes(role.id) ? (
                      <Checkbox
                        checked
                        disabled
                        readOnly
                        onChange={() => {}}
                        name={`checkbox_${user?.userId || "unknown"}_${role?.id || "unknown"}`}
                        aria-label={`Role for ${user?.displayName || "unknown"} as ${role?.name || "unknown"}`}
                      />
                    ) : (
                      <CircleXIcon
                        aria-label={`No role for ${user?.displayName || "unknown"} as ${role?.name || "unknown"}`}
                        className="circle-x-icon"
                      />
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="pagination-container">
          <UserRoleManagementPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredUsers.length}
            pageSize={pageSize}
            loading={false}
            onPageChange={setCurrentPage}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setCurrentPage(1);
            }}
            pageSizeOptions={[10, 20]}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  allRoles: state.UserRoleManagementReducer.allRolesList || [],
  entityUserRoles: state.UserRoleManagementReducer.entityUserRolesList || [],
});

const ConnectedUserRoleManagement = WithNavigationToHome(
  connect(mapStateToProps)(UserRoleManagement)
);

const UserRoleManagementContainer = (props) =>
  props.auth?.isAuthenticated ? (
    <ConnectedUserRoleManagement {...props} />
  ) : (
    <div />
  );

export default withAuth(UserRoleManagementContainer);